import { useState } from "react";
import { downloadBlob, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapTrifold, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MapStudioPanel({ project }) {
  const id = project.id;
  const layers = project.layers || {};
  const opts = [];
  if (project.prospectivity) opts.push(["prospectivity", "Mineral Prospectivity"]);
  if (project.prospectivity_ml) opts.push(["prospectivity_ml", "ML Prospectivity"]);
  Object.keys(layers).forEach((k) => opts.push([k, k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())]));
  if (project.terrain) ["elevation", "hillshade", "slope", "aspect", "drainage"].forEach((k) => opts.push([`terrain:${k}`, `Terrain - ${k}`]));
  if (project.lineaments) opts.push(["lineaments", "Lineament Density"]);

  const [form, setForm] = useState({
    layer: opts[0]?.[0] || "prospectivity", page: "A4", orientation: "landscape",
    title: "", subtitle: "", prepared_by: "", reviewed_by: "", map_number: "", notes: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!opts.length) return toast.error("Generate a map layer (indices or terrain) first");
    setBusy(true);
    try {
      const q = new URLSearchParams(form).toString();
      await downloadBlob(`/projects/${id}/mapstudio?${q}`, `SYMIN_map_${form.layer}.pdf`);
      toast.success("Map composed");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div><Label className="text-xs">Map Layer</Label>
        <Select value={form.layer} onValueChange={set("layer")}>
          <SelectTrigger className="h-9 mt-1" data-testid="ms-layer"><SelectValue placeholder="Select layer" /></SelectTrigger>
          <SelectContent className="max-h-64">{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Page Size</Label>
          <Select value={form.page} onValueChange={set("page")}>
            <SelectTrigger className="h-9 mt-1" data-testid="ms-page"><SelectValue /></SelectTrigger>
            <SelectContent>{["A4", "A3", "A2", "A1"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Orientation</Label>
          <Select value={form.orientation} onValueChange={set("orientation")}>
            <SelectTrigger className="h-9 mt-1" data-testid="ms-orient"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="landscape">Landscape</SelectItem><SelectItem value="portrait">Portrait</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={set("title")} className="h-9 mt-1" data-testid="ms-title" placeholder="Gold Prospectivity Map" /></div>
      <div><Label className="text-xs">Subtitle</Label><Input value={form.subtitle} onChange={set("subtitle")} className="h-9 mt-1" data-testid="ms-subtitle" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Prepared by</Label><Input value={form.prepared_by} onChange={set("prepared_by")} className="h-9 mt-1" data-testid="ms-prep" /></div>
        <div><Label className="text-xs">Reviewed by</Label><Input value={form.reviewed_by} onChange={set("reviewed_by")} className="h-9 mt-1" data-testid="ms-rev" /></div>
      </div>
      <div><Label className="text-xs">Map Number</Label><Input value={form.map_number} onChange={set("map_number")} className="h-9 mt-1" data-testid="ms-num" /></div>
      <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} className="mt-1" data-testid="ms-notes" /></div>
      <Button className="w-full" onClick={generate} disabled={busy} data-testid="ms-generate">
        {busy ? <><CircleNotch size={16} className="mr-1 animate-spin" />Composing…</> : <><MapTrifold size={16} className="mr-1" />Compose Print-Ready Map (PDF)</>}
      </Button>
      <p className="text-[11px] text-muted-foreground">Composed A-series layout includes title block, legend, north arrow, scale bar, CRS, consultancy branding and the scientific disclaimer.</p>
    </div>
  );
}
